import type { ControlClientMessage } from '@/contracts';

export type MotionVelocity = {
  x: number;
  y: number;
  z: number;
};

export type GamepadCommandKind = 'motion' | 'grab' | 'other';

export type BufferedAmountThresholdDirection = 'up' | 'down';

export type BufferedAmountThresholdCrossing = {
  direction: BufferedAmountThresholdDirection;
  thresholdBytes: number;
};

export const MOTION_DIAGNOSTIC_SAMPLE_RATE = 20;

export const MOTION_BUFFERED_AMOUNT_THRESHOLDS = [1024, 4096, 16384] as const;

const isGamepadHandlerMessage = (
  message: ControlClientMessage,
): message is Extract<
  ControlClientMessage,
  { handler: 'gamepad' }
> => {
  const hasHandler = 'handler' in message;
  if (!hasHandler) {
    return false;
  }

  return message.handler === 'gamepad';
};

const getOrderedThresholds = (
  previousBufferedAmountBytes: number,
  currentBufferedAmountBytes: number,
): number[] => {
  const isIncreasing =
    currentBufferedAmountBytes >= previousBufferedAmountBytes;
  if (isIncreasing) {
    return [...MOTION_BUFFERED_AMOUNT_THRESHOLDS];
  }

  return [...MOTION_BUFFERED_AMOUNT_THRESHOLDS].reverse();
};

export const getGamepadCommandKind = (
  message: ControlClientMessage,
): GamepadCommandKind => {
  if (!isGamepadHandlerMessage(message)) {
    return 'other';
  }

  if (message.type === 'motion_intent') {
    return 'motion';
  }

  if (message.type === 'grab') {
    return 'grab';
  }

  return 'other';
};

export const getBufferedAmountThresholdCrossings = (
  previousBufferedAmountBytes: number,
  currentBufferedAmountBytes: number,
): BufferedAmountThresholdCrossing[] => {
  const orderedThresholds = getOrderedThresholds(
    previousBufferedAmountBytes,
    currentBufferedAmountBytes,
  );
  const thresholdCrossings: BufferedAmountThresholdCrossing[] = [];

  orderedThresholds.forEach((thresholdBytes) => {
    const wasAboveThreshold = previousBufferedAmountBytes >= thresholdBytes;
    const isAboveThreshold = currentBufferedAmountBytes >= thresholdBytes;
    if (wasAboveThreshold === isAboveThreshold) {
      return;
    }

    const direction: BufferedAmountThresholdDirection = isAboveThreshold
      ? 'up'
      : 'down';
    thresholdCrossings.push({
      direction,
      thresholdBytes,
    });
  });

  return thresholdCrossings;
};

export const isZeroVelocity = (velocity: MotionVelocity): boolean => {
  const isZeroX = velocity.x === 0;
  const isZeroY = velocity.y === 0;
  const isZeroZ = velocity.z === 0;
  return isZeroX && isZeroY && isZeroZ;
};

export const shouldLogSample = (
  sampleCount: number,
  sampleRate: number,
): boolean => {
  const hasPositiveRate = sampleRate > 0;
  if (!hasPositiveRate) {
    return false;
  }

  return sampleCount % sampleRate === 0;
};
